import styled from '@emotion/styled';

/**
 * "분석 Report" 탭 스타일.
 *
 * 원본은 ref/html/ut_matrix_rpt_demo.html의 분석 Report 탭(1074~1124줄)이고,
 * ref/frontend/features/UtMatrixRpt/styles.ts에서 이 탭에 필요한 규칙만 옮겼다.
 * 전역 CSS와 섞이지 않도록 Root로 스코핑한다(UtComparison/UtComparisonElec와 동일 방식).
 */
export const AnalysisReportRoot = styled.div`
  color: #1f1f1f;
  font-size: 13px;

  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #135678;
    margin: 16px 0 10px;
  }
  .section-title:first-of-type {
    margin-top: 4px;
  }
  .section-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .section-title-row .section-title {
    flex: 1;
  }
  .section-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .report-panel {
    border: 1px solid #dde0df;
    border-radius: 3px;
    background: #fff;
    padding: 16px 20px;
  }

  .notice {
    border: 1px solid #8bf0e9;
    background: #ecfcf8;
    color: #135678;
    border-radius: 3px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 12px;
  }
  .loading {
    padding: 48px 20px;
    text-align: center;
    color: #2d9bb2;
    font-size: 12px;
  }
  .error-box {
    border: 1px solid #fda293;
    background: #fef1eb;
    color: #b32542;
    border-radius: 3px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 12px;
    white-space: pre-wrap;
    word-break: break-all;
  }
  .empty {
    padding: 48px 20px;
    text-align: center;
    color: #979998;
    font-size: 12px;
    line-height: 1.8;
  }

  /* ── 버튼 ── */
  .btn-mode {
    height: 32px;
    padding: 6px 14px;
    border: 1px solid #dde0df;
    border-radius: 2px;
    background: #fff;
    color: #5b5c5b;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-mode:hover {
    background: #f1f4f3;
    border-color: #979998;
  }
  .btn-primary {
    height: 32px;
    padding: 6px 14px;
    border: none;
    border-radius: 2px;
    background: #2d9bb2;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-primary:hover {
    background: #135678;
  }
  .btn-mode:disabled,
  .btn-primary:disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .report-format-select {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 16px 0 0;
    margin-right: 4px;
    border-right: 1px solid #dde0df;
    font-size: 13px;
    color: #5b5c5b;
  }
  .report-format-select label {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-weight: 700;
  }
  .report-format-select input[type='radio'] {
    cursor: pointer;
  }

  /* ── 대상 UT Matrix 뱃지 ── */
  .target-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 700;
    margin-right: 8px;
    white-space: nowrap;
  }
  .target-badge .role-tag {
    font-size: 10px;
    padding: 0 4px;
    border-radius: 2px;
  }
  .target-badge.role-mst {
    background: #ecfcf8;
    color: #135678;
    border: 1px solid #2d9bb2;
  }
  .target-badge.role-tgt {
    background: #fff7e8;
    color: #a15c00;
    border: 1px solid #e8a33d;
  }
  .target-badge.role-ref {
    background: #f3effc;
    color: #554596;
    border: 1px solid #a897df;
  }

  /* ── 표시 항목 필터 ── */
  .filter-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .filter-label {
    font-size: 12px;
    font-weight: 700;
    color: #5b5c5b;
    width: 36px;
    flex-shrink: 0;
  }
  .filter-btn {
    height: 26px;
    padding: 0 10px;
    border: 1px solid #dde0df;
    border-radius: 2px;
    background: #fff;
    color: #5b5c5b;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
  .filter-btn.active {
    background: #2d9bb2;
    border-color: #2d9bb2;
    color: #fff;
  }
  .filter-btn:hover:not(.active) {
    border-color: #2d9bb2;
    color: #2d9bb2;
  }
  .filter-btn:disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  /* ── 표 ── */
  table {
    border-collapse: collapse;
    width: 100%;
  }
  thead th {
    background: #f1f4f3;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    padding: 7px 8px;
    border-right: 1px solid #dde0df;
    border-bottom: 1px solid #dde0df;
    white-space: nowrap;
  }
  thead th:last-child {
    border-right: none;
  }
  tbody td {
    padding: 6px 8px;
    font-size: 12px;
    border-right: 1px solid #f1f4f3;
    border-bottom: 1px solid #f1f4f3;
  }
  tbody td:last-child {
    border-right: none;
  }
  td.center {
    text-align: center;
  }

  /*
    UT 항목을 많이 켜면 열이 늘어 표가 패널 폭을 넘는다. 헤더가 nowrap이라(위 thead th)
    열이 줄어들지 못해서, 예전에는 표가 패널을 그대로 뚫고 나갔다. 넘칠 때만 가로 스크롤.

    ⚠ PDF 발행은 이 영역을 html2canvas로 캡쳐하는데(exportPdf.ts), 스크롤 컨테이너는
      보이는 부분만 찍힌다. 그래서 발행 직전에 .pdf - capturing을 붙여 스크롤을 풀고
      표 전체가 펼쳐진 상태로 캡쳐한다(AnalysisReportPanel.handlePublish).
  */
  .sum-table {
    margin-bottom: 20px;
    overflow-x: auto;
  }
  .report-panel.pdf-capturing .sum-table {
    overflow-x: visible;
  }
  .sum-table tr.diff-row td {
    background: #f7f9f8;
    font-weight: 700;
  }
  .sum-table td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .sum-table td.num.neg {
    color: #b32542;
  }

  /* ── 검토 내역 입력 ── */
  .review-table table {
    table-layout: fixed;
  }
  .review-table tbody td {
    padding: 8px;
    vertical-align: top;
  }
  /*
    RVW_CTN 등이 VARCHAR(4000)이라 짧은 입력칸으로는 내용을 확인하기 어렵다.
    너비는 표 레이아웃(table-layout: fixed)에 묶여 있으므로 높이만 키운다.
    더 길게 쓰려면 사용자가 우하단을 끌어 늘릴 수 있다(resize: vertical).
  */
  .rvw-textarea {
    width: 100%;
    min-height: 100px;
    padding: 4px 6px;
    border: 1px solid #b9bcbb;
    border-radius: 2px;
    font-size: 12px;
    font-family: inherit;
    line-height: 1.5;
    resize: vertical;
  }
  .rvw-textarea:focus {
    outline: none;
    border-color: #3ec2cf;
  }
  .rvw-textarea:disabled {
    background: #f7f9f8;
    color: #979998;
  }
`;
